import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Gamepad2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import MatchForm from '../components/MatchForm';

const Matches = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [filter, setFilter] = useState({ tournament_id: '', status: '', team_id: '' });
  const [sorting, setSorting] = useState({ sort_by: 'scheduled_date', sort_order: 'asc' });
  const queryClient = useQueryClient();

  const { data: matches = [], isLoading } = useQuery({
    queryKey: ['matches', filter, sorting],
    queryFn: () => {
      const cleanFilter = Object.fromEntries(
        Object.entries({ ...filter, ...sorting }).filter(([_, value]) => value && value.toString().trim() !== '')
      );
      return api.getMatches(cleanFilter);
    }
  });
  const { data: tournaments = [] } = useQuery({ queryKey: ['tournaments'], queryFn: api.getTournaments });
  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: api.getTeams });
  const { data: umpires = [] } = useQuery({ queryKey: ['umpires'], queryFn: api.getUmpires });

  const createMutation = useMutation({
    mutationFn: api.createMatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      setShowForm(false);
      toast.success('Match created successfully!');
    },
    onError: () => toast.error('Failed to create match'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.updateMatch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      setShowForm(false);
      setEditingMatch(null);
      toast.success('Match updated successfully!');
    },
    onError: () => toast.error('Failed to update match'),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteMatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      toast.success('Match deleted successfully!');
    },
    onError: () => toast.error('Failed to delete match'),
  });

  const handleSubmit = (data) => {
    // Clean the data - convert empty strings to null and remove empty fields
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key, 
        value === '' || value === null ? null : value
      ]).filter(([_, value]) => value !== null)
    );
    
    if (editingMatch) {
      updateMutation.mutate({ id: editingMatch.id, data: cleanData });
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const handleEdit = (match) => {
    setEditingMatch(match);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this match?')) {
      deleteMutation.mutate(id);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      live: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      abandoned: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cricket-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Matches</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-cricket-green text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4" />
          <span>Create Match</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tournament</label>
            <select
              value={filter.tournament_id}
              onChange={(e) => setFilter({ ...filter, tournament_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Tournaments</option>
              {tournaments.map(tournament => (
                <option key={tournament.id} value={tournament.id}>{tournament.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
            <select
              value={filter.team_id}
              onChange={(e) => setFilter({ ...filter, team_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sorting.sort_by}
              onChange={(e) => setSorting({ ...sorting, sort_by: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="scheduled_date">Match Date</option>
              <option value="match_number">Match Number</option>
              <option value="created_at">Date Added</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select
              value={sorting.sort_order}
              onChange={(e) => setSorting({ ...sorting, sort_order: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="asc">Earliest First</option>
              <option value="desc">Latest First</option>
            </select>
          </div>
        </div>
      </div>

      {showForm ? (
        <MatchForm
          match={editingMatch}
          tournaments={tournaments}
          teams={teams}
          umpires={umpires}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingMatch(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      ) : (
        <div className="space-y-4">
        {matches.map((match) => (
          <div key={match.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-cricket-green rounded-full flex items-center justify-center">
                  <Gamepad2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Match #{match.match_number}
                  </h3>
                  <p className="text-sm text-gray-600">{match.format}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(match)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(match.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(match.status)}`}>
                    {match.status.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="text-sm font-medium">{formatDate(match.scheduled_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Venue:</span>
                  <span className="text-sm font-medium">{match.venue}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tournament:</span>
                  <span className="text-sm font-medium">
                    {tournaments.find(t => t.id === match.tournament_id)?.name || 'Unknown'}
                  </span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-800 mb-2">Teams</div>
                  <div className="flex items-center justify-center space-x-3">
                    <span className="text-sm font-medium">{getTeamName(match.team1_id)}</span>
                    <span className="text-xs text-gray-500 font-semibold">VS</span>
                    <span className="text-sm font-medium">{getTeamName(match.team2_id)}</span>
                  </div>
                </div>
                
                {match.toss_winner && (
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Toss Winner</div>
                    <div className="text-sm font-medium">{getTeamName(match.toss_winner)}</div>
                    <div className="text-xs text-gray-500">Chose to {match.toss_decision}</div>
                  </div>
                )}
              </div>
            </div>

            {match.umpires && match.umpires.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">Umpires: {match.umpires.join(', ')}</div>
              </div>
            )}
          </div>
        ))}
        </div>
      )}

      {!showForm && matches.length === 0 && (
        <div className="text-center py-12">
          <Gamepad2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No matches found</h3>
          <p className="text-gray-500 mb-4">Create your first match to get started</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-cricket-green text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
          >
            Create Match
          </button>
        </div>
      )}
    </div>
  );
};

export default Matches;
